/* eslint-disable react/prop-types */
import './index.scss';
import ModalBase from '.';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Alert, Button, Input, Select } from '@piggytechng/component-library';
import { OverideButtonStyle, OverideInputStyle, OverideSelectStyle } from '../../helpers/utils';
import { ModalTransactionSearchProps } from '../../types';

export const TRANSACTION_SEARCH_CRITERIA = [
  { label: 'Reference', value: 'reference' },
  { label: 'Balance', value: 'balance' },
  { label: 'Date', value: 'date' },
  { label: 'Date Range', value: 'date-range' },
  { label: 'Memo', value: 'memo' },
];

const API_DATE = 'YYYY/MM/DD'; // format the backend expects in the query
const INPUT_DATE = 'YYYY-MM-DD'; // format <input type="date"> uses
const DISPLAY_DATE = 'D MMM YYYY'; // human-readable label for the toolbar chip

function ModalTransactionSearch({
  modalIsOpen,
  setIsOpen,
  onApply,
  onClear,
  isLoading = false,
  hasCriteria = true,
  criteriaOptions = TRANSACTION_SEARCH_CRITERIA,
  initialFilter = '',
  initialQuery = '',
}: ModalTransactionSearchProps) {
  const SelectStyle = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const [filter, setFilter] = useState(initialFilter);
  const [query, setQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const isDateFilter = hasCriteria && filter === 'date';
  const isDateRangeFilter = hasCriteria && filter === 'date-range';

  useEffect(() => {
    if (!modalIsOpen) return;

    setStartDate('');
    setEndDate('');

    // The API stores the date filter as 'date' for both single dates and ranges; detect a range
    // by the '-' separating two YYYY/MM/DD values and restore the right UI.
    if (initialFilter === 'date' && initialQuery.includes('-')) {
      const [start, end] = initialQuery.split('-');
      setFilter('date-range');
      setStartDate(start ? dayjs(start).format(INPUT_DATE) : '');
      setEndDate(end ? dayjs(end).format(INPUT_DATE) : '');
      setQuery('');
    } else if (initialFilter === 'date' && initialQuery) {
      setFilter('date');
      setQuery(dayjs(initialQuery).format(INPUT_DATE));
    } else {
      setFilter(initialFilter);
      setQuery(initialQuery);
    }
  }, [modalIsOpen]);

  function closeModal() {
    setIsOpen(false);
  }

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasCriteria && !filter) {
      Alert.error('Please select a search criteria');
      return;
    }

    let value = '';
    let display = '';
    // Both single date and date range are sent under the 'date' filter.
    let apiFilter = filter;

    if (isDateRangeFilter) {
      if (!startDate || !endDate) {
        Alert.error('Please select both start and end dates');
        return;
      }
      if (dayjs(endDate).isBefore(dayjs(startDate))) {
        Alert.error('End date cannot be before start date');
        return;
      }
      apiFilter = 'date';
      value = `${dayjs(startDate).format(API_DATE)}-${dayjs(endDate).format(API_DATE)}`;
      display = `${dayjs(startDate).format(DISPLAY_DATE)} - ${dayjs(endDate).format(DISPLAY_DATE)}`;
    } else if (isDateFilter) {
      if (!query) {
        Alert.error('Please select a date');
        return;
      }
      value = dayjs(query).format(API_DATE);
      display = dayjs(query).format(DISPLAY_DATE);
    } else {
      if (!query.trim()) {
        Alert.error('Please enter a search value');
        return;
      }
      value = query.trim();
      display = query.trim();
    }

    onApply(apiFilter, value, display);
  };

  const handleClear = () => {
    setFilter('');
    setQuery('');
    setStartDate('');
    setEndDate('');
    onClear();
    setIsOpen(false);
  };

  const selectedLabel = criteriaOptions.find((option) => option.value === filter)?.label;

  return (
    <ModalBase modalIsOpen={modalIsOpen} setIsOpen={setIsOpen}>
      <div className={`modal-processor`}>
        <div className="modal-processor__header">
          <h2>Search transactions</h2>
          <button onClick={closeModal}>&times;</button>
        </div>

        <form className="modal-processor__body" onSubmit={handleApply}>
          {hasCriteria && (
            <Select
              {...SelectStyle}
              labelStyle={{ color: '#666766' }}
              options={criteriaOptions}
              backgroundColor="#F9FAFB"
              outlineColor="none"
              label="Search with"
              placeholder="Select search criteria"
              value={filter}
              onChange={(value: string) => {
                setFilter(value);
                setQuery('');
                setStartDate('');
                setEndDate('');
              }}
            />
          )}

          {isDateRangeFilter ? (
            <>
              <Input
                {...OverideInputStyle}
                labelStyle={{ color: '#666766' }}
                label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                {...OverideInputStyle}
                labelStyle={{ color: '#666766' }}
                label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </>
          ) : (
            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Search value"
              type={isDateFilter ? 'date' : 'text'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                hasCriteria
                  ? filter
                    ? `Enter ${selectedLabel?.toLowerCase()}`
                    : 'Enter search value'
                  : 'Search by reference, amount, memo, date'
              }
            />
          )}

          <div className="modal-processor__actions">
            <button type="button" onClick={handleClear} className="modal-processor__actions-reject">
              Clear
            </button>
            <Button
              {...OverideButtonStyle}
              className="modal-processor__actions-resolve"
              type="submit"
              loading={isLoading}
            >
              Apply
            </Button>
          </div>
        </form>
      </div>
    </ModalBase>
  );
}

export default ModalTransactionSearch;
